/*
  Apple Books — hero slideshow

  Moves a flex track by whole viewport widths. No library: the whole job is
  an index, a transform, and the manners a carousel needs to not be annoying.

  Those manners, in order of how often they are got wrong:

  - It stops. Hovering it, tabbing into it, or switching tabs pauses the
    timer, and a reader who has asked for reduced motion never gets one.
  - The slides that are not showing are hidden from screen readers, and the
    links inside them are taken out of the tab order, so tabbing does not
    disappear into artwork nobody can see.
  - It can be swiped, and a swipe that turns out to be a vertical scroll is
    left alone rather than hijacked.
*/

class APHeroSlides extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.ap-slides__track');
    this.slides = Array.from(this.querySelectorAll('.ap-slides__slide'));
    this.dots = Array.from(this.querySelectorAll('.ap-slides__dot'));
    if (!this.track || this.slides.length < 2) return;

    this.index = 0;
    this.timer = null;
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.interval = Math.max(3000, parseInt(this.dataset.interval, 10) || 6000);
    this.wantsAutoplay = this.dataset.autoplay === 'true';

    this.querySelector('.ap-slides__arrow--prev')
      ?.addEventListener('click', () => this.step(-1, true));
    this.querySelector('.ap-slides__arrow--next')
      ?.addEventListener('click', () => this.step(1, true));

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => this.go(Number(dot.dataset.index), true));
    });

    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.step(-1, true); }
      if (e.key === 'ArrowRight') { e.preventDefault(); this.step(1, true); }
    });

    // Pause while someone is actually looking at it.
    this.addEventListener('pointerenter', () => this.stop());
    this.addEventListener('pointerleave', () => this.start());
    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', () => {
      if (!this.contains(document.activeElement)) this.start();
    });

    this.onVisibility = () => (document.hidden ? this.stop() : this.start());
    document.addEventListener('visibilitychange', this.onVisibility);

    this.addSwipe();

    // Someone can turn reduced motion on while the page is open.
    this.onMotionChange = () => (this.reduced.matches ? this.stop() : this.start());
    this.reduced.addEventListener('change', this.onMotionChange);

    this.paint();
    this.start();
  }

  disconnectedCallback() {
    this.stop();
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.reduced?.removeEventListener('change', this.onMotionChange);
  }

  go(i, byHand) {
    const count = this.slides.length;
    this.index = ((i % count) + count) % count;
    this.paint();
    // A deliberate press restarts the clock, so the slide does not change
    // half a second after being chosen.
    if (byHand) this.restart();
  }

  step(by, byHand) {
    this.go(this.index + by, byHand);
  }

  paint() {
    this.track.style.transform = 'translate3d(' + -this.index * 100 + '%, 0, 0)';

    this.slides.forEach((slide, i) => {
      const showing = i === this.index;
      slide.toggleAttribute('aria-hidden', !showing);
      const link = slide.querySelector('a');
      if (link) {
        if (showing) link.removeAttribute('tabindex');
        else link.setAttribute('tabindex', '-1');
      }
    });

    this.dots.forEach((dot, i) => {
      const showing = i === this.index;
      dot.classList.toggle('is-current', showing);
      if (showing) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  start() {
    if (!this.wantsAutoplay || this.reduced.matches || document.hidden) return;
    if (this.contains(document.activeElement)) return;
    this.stop();
    this.timer = window.setInterval(() => this.step(1, false), this.interval);
  }

  stop() {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  restart() {
    this.stop();
    this.start();
  }

  addSwipe() {
    let startX = 0;
    let startY = 0;
    let tracking = false;

    this.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse') return;
      tracking = true;
      startX = e.clientX;
      startY = e.clientY;
    }, { passive: true });

    this.addEventListener('pointerup', (e) => {
      if (!tracking) return;
      tracking = false;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      // Sideways, and meant: anything flatter than this is a scroll.
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
      this.step(dx < 0 ? 1 : -1, true);
    }, { passive: true });

    this.addEventListener('pointercancel', () => { tracking = false; }, { passive: true });
  }
}

if (!customElements.get('ap-hero-slides')) {
  customElements.define('ap-hero-slides', APHeroSlides);
}
